import { commitSchema } from './commit'
import { generate } from './generate'
import { baseSchema, Update } from './base'
import { z } from 'zod'
import { inviteSchema } from './invite-base'

const tokenScopeEnum = z.enum(['read', 'write'])

/* -------------------------------------------------------------------------- */
/*                                 Integration                                */
/* -------------------------------------------------------------------------- */

export const integrationSchema = z.object({
  id: z.string(),
  type: z.enum(['vercel']),
  token: z.object({
    token: z.string(),
    type: z.enum(['Bearer']),
  }),
  configurationId: z.string().nullish(),
  teamId: z.string().nullish(), // teamId is the vercel teamId,
  tokenScope: tokenScopeEnum.nullish(),
})

export type Integration = z.infer<typeof integrationSchema>
export type IntegrationUpdate = Update<Integration>

export const integrationBaseOps = generate(
  'integration',
  integrationSchema.parse
)
export const getIntegrationKey = (id: string) => `integration/${id}`

/* -------------------------------------------------------------------------- */
/*                                    Repo                                    */
/* -------------------------------------------------------------------------- */

export const tokenSchema = z.object({
  token: z.string(),
  scope: tokenScopeEnum,
})
export type RepoToken = z.infer<typeof tokenSchema>

export const repoForkStatusSchema = z.object({
  fromRepoId: z.string(),
  lastSyncedAt: z.string(),
  fromBranchId: z.string().nullish(),
  fromCommitId: z.string().nullish(),
})
export type RepoForkStatus = z.infer<typeof repoForkStatusSchema>

export const vercelDeployButtonSchema = z.object({
  isEnabled: z.boolean(),
  gitRepoUrl: z.string(),
  demoUrl: z.string().nullish(),
  envVars: z.string().nullish(),
  tokenScope: tokenScopeEnum.nullish(),
})
export type VercelDeployButtonConfig = z.infer<typeof vercelDeployButtonSchema>

export const readmeScreenshotsSchema = z.array(
  z.object({
    id: z.string(),
    alt: z.string(),
    url: z.string(),
    width: z.number(),
    height: z.number(),
    mimeType: z.string(),
  })
)

export type READMEScreenshots = z.infer<typeof readmeScreenshotsSchema>

export const readmeAdditionalInfoSchema = z.object({
  overview: z.string().optional(),
  useCase: z.array(z.string()),
  framework: z.array(z.string()),
  techStack: z.array(z.string()),
})

export type READMEAdditionalInfo = z.infer<typeof readmeAdditionalInfoSchema>

export const guestSchema = z.object({
  userId: z.string(),
  createdAt: z.string(),
})

export type Guest = z.infer<typeof guestSchema>

export const agentsConfigSchema = z.object({
  isOpenRouterConnected: z.boolean().nullish(),
  openRouterApiKey: z.string().nullish(),
  availableModels: z.array(
    z.object({ openrouterId: z.string(), label: z.string() })
  ),
})
export type AvailableModel = z.infer<
  typeof agentsConfigSchema.shape.availableModels
>[number]
export type AgentsConfig = z.infer<typeof agentsConfigSchema>

// Extended AvailableModel with hide property for FREE_MODELS
export type AvailableModelWithHide = AvailableModel & { hide?: boolean }

// Free models available to all users
export const FREE_MODELS = [
  { openrouterId: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { openrouterId: 'openai/gpt-4.1-mini', label: 'GPT-4.1 Mini' },
  { openrouterId: 'deepseek/deepseek-chat-v3.1', label: 'DeepSeek V3.1' },
] satisfies AvailableModelWithHide[]

// Premium models suggested for OpenRouter users
export const PREMIUM_MODELS = [
  { openrouterId: 'anthropic/claude-sonnet-4.5', label: 'Claude Sonnet 4.5' },
  { openrouterId: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  { openrouterId: 'openai/gpt-4.1', label: 'GPT-4.1' },
  { openrouterId: 'openai/gpt-4o', label: 'GPT-4o' },
  { openrouterId: 'x-ai/grok-4', label: 'Grok 4' },
  { openrouterId: 'openai/gpt-5', label: 'GPT-5' },
] satisfies AvailableModel[]

export const DEFAULT_FREE_MODEL = 'google/gemini-2.5-flash'
export const DEFAULT_START_MODEL = 'anthropic/claude-sonnet-4.5'

/**
 * Merges available models with free and premium models based on selection logic
 * @param availableModels - Currently selected models from repository
 * @param hasOpenRouterKey - Whether user has OpenRouter API key for premium models
 * @returns Array of models that should be selected
 */
export const mergeWithFreeModels = (
  availableModels: AvailableModel[],
  hasOpenRouterKey: boolean = false
): AvailableModel[] => {
  const freeModelsOnly = (FREE_MODELS as AvailableModelWithHide[]).filter(
    (model) => !model.hide
  )
  const premiumModelsOnly = hasOpenRouterKey ? PREMIUM_MODELS : []
  const defaultModels = [...freeModelsOnly, ...premiumModelsOnly]

  const selectedDefaultModels = availableModels.filter((model) =>
    defaultModels.some(
      (defaultModel) => defaultModel.openrouterId === model.openrouterId
    )
  )
  const selectedOtherModels = availableModels.filter(
    (model) =>
      !defaultModels.some(
        (defaultModel) => defaultModel.openrouterId === model.openrouterId
      )
  )

  // If no default models are selected, merge in all default models
  if (selectedDefaultModels.length === 0) {
    return [...selectedOtherModels, ...defaultModels]
  }

  // If all default models are selected, remove them all (go back to original behavior)
  if (selectedDefaultModels.length === defaultModels.length) {
    return selectedOtherModels
  }

  // Otherwise, respect the current selection
  return availableModels
}

/**
 * Validates if a model can be used based on the repository configuration
 * @param modelId - The OpenRouter model ID to validate
 * @param repository - The repository containing agents config
 * @returns Object with the valid model ID and state
 */
export const validateModel = (
  modelId: string,
  repository: Repository
): {
  validModel: string
  state: 'valid' | 'fallback'
} => {
  // Check if it's a free model (always valid)
  const isFreeModel = FREE_MODELS.some(
    (model) => model.openrouterId === modelId
  )
  if (isFreeModel) {
    return { validModel: modelId, state: 'valid' }
  }

  // Check if user has OpenRouter API key for premium models
  const hasOpenRouterKey = !!repository.agentsConfig?.openRouterApiKey
  if (hasOpenRouterKey) {
    return { validModel: modelId, state: 'valid' }
  }

  // Fallback to first free model if premium model can't be used
  const fallbackModel =
    repository.agentsConfig?.availableModels.find((model) =>
      FREE_MODELS.some((fm) => fm.openrouterId === model.openrouterId)
    )?.openrouterId ?? FREE_MODELS[0]!.openrouterId
  return { validModel: fallbackModel, state: 'fallback' }
}

export const repositorySchema = baseSchema.extend({
  migrated: z.boolean().nullish(),
  migrationNumber: z
    .union([z.string(), z.number()])
    .transform((val) => Number(val))
    .optional(),
  title: z.string(),
  description: z.string().nullish(),
  slug: z.string(),
  createdAt: z.string(),
  lastTransferredAt: z.string().nullish(),
  forkStatus: repoForkStatusSchema.nullish(),
  isPublic: z.boolean().nullish().default(false),
  stats: z
    .object({
      bookmarks: z.number(),
      forks: z.number(),
    })
    .nullish(),
  latestCommit: commitSchema.nullable(),
  contributors: z.array(z.string()),
  tokens: tokenSchema.array(),
  status: z
    .enum(['active', 'transferring', 'deleted'])
    .optional()
    .nullable()
    .default('active'),
  previewUrl: z.string().nullish(),
  gitRepoUrl: z.string().nullish(),
  revalidateURLs: z.string().array().nullish(),
  assistantId: z.string().nullish(),
  readme: z
    .object({
      vercelDeployButton: vercelDeployButtonSchema.nullish(),
      screenshots: readmeScreenshotsSchema.nullish(),
      additionalInfo: readmeAdditionalInfoSchema.nullish(),
    })
    .nullish(),
  integrations: z
    .array(
      z.object({
        id: integrationSchema.shape.id,
        type: integrationSchema.shape.type,
        projectId: z.string().optional(), // projectId is the vercel projectId
      })
    )
    .nullish(),
  branching: z
    .object({
      archiveAfterMerging: z.boolean(),
    })
    .optional(),
  guests: z
    .object({
      invites: inviteSchema.array(),
      list: guestSchema.array(),
    })
    .nullish(),
  // used for rendering "connect to your app" helpers specific to where the user came from
  rel: z.enum(['playground-v0', 'playground-v0-admin']).nullish(),
  agentsConfig: agentsConfigSchema.nullish(),
})

export type Repository = z.infer<typeof repositorySchema>
export type RepositoryUpdate = Update<Repository>

export const repositoryBaseOps = generate('repository', repositorySchema.parse)
export const getRepositoryKey = (id: string) => `repository/${id}`

export const extractRelFromPlaygroundId = (
  playgroundId: string | undefined | null
): Repository['rel'] => {
  if (!playgroundId) return undefined

  const [, rel] = playgroundId.split('__rel_')
  switch (rel) {
    case 'v0':
      return 'playground-v0'
    case 'v0-admin':
      return 'playground-v0-admin'
    default:
      return undefined
  }
}
